// eslint-disable-next-line no-unused-vars
import React from 'react';
import { bool, func, object, string } from 'prop-types';
import styled from '@emotion/styled';
import { ComposedModal, ModalHeader, ModalBody, Button, Loading } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import { Megaphone } from 'lucide-react';
import { useTheme } from '@emotion/react';

import { EMAIL_REGEX } from 'Constants';
import { NWTextInput, NWCheckbox } from 'Components/shared';
import ModalManager from 'Containers/ModalManager/ModalManager';

const StyledComposedModal = styled(ComposedModal)`
    z-index: 9001;
`;

const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0;
  margin-bottom: 30px;
  color: #3a3a3a;

  .alert {
    margin-top: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 90px;
    height: 90px;
    background: #fff2f1;
    border-radius: 50%;

    svg {
      height: 45px;
      width: 45px;
    }
  }

  .header {
    font-size: 30px;
    padding: 18px 32px;
    
    p span {
      font-weight: 700;
    }
  }

  p {
      padding-right: 0 !important;
  }

  .danger {
    background: #F3F2F2;
    padding: 24px 32px;
    margin-bottom: 24px;
    width: 100%;

    .checkbox {
      margin-bottom: 0;

      label span {
        text-align: left;
      }
    }

    .cds--text-input-wrapper {
      margin: 0 0 0 24px;
    }
  }

  .actions {
    display: flex;
    gap: 24px;

    button {
      min-width: 0;
      height: 35px;
      width: 180px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      border-color: inherit;

      &.cancel {
        color: #D52B1E;
        border: 1px solid #D52B1E;
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    .actions {
      flex-direction: column;
    }
  }
`;

const StyledLoading = styled(Loading)`
  width: 1em;
  height: 1em;
  margin-left: 1em;
  left: 90px !important;
`;

const StockAlertModal = ({
  open,
  code,
  primaryEmail,
  setPrimaryChecked,
  primaryChecked,
  additionalEmail,
  additionalChecked,
  setAdditionalChecked,
  isLoading,
  handleConfirm,
  handleClose,
  register,
  errors,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <ModalManager open={ open }>
      <StyledComposedModal
        size="sm"
        onClose={ handleClose }
        selectorPrimaryFocus=".cds--modal-content"
      >
        <ModalHeader />
        <StyledModalBody theme={ theme }>
          <div className="alert">
            <Megaphone size={32} />
          </div>
          <div className="header">
            <h2>{ t('products.stockAlert') }</h2>
            <p dangerouslySetInnerHTML={ { __html: t('profile.stockAlertModalText', { code }) } } />
          </div>
          <div className="danger">
            <NWCheckbox
              id="primaryChecked"
              name="primaryChecked"
              className="checkbox"
              labelText={ primaryEmail }
              checked={ primaryChecked }
              onChange={ setPrimaryChecked }
            />
            <NWCheckbox
              id="additional"
              name="additional"
              className="checkbox"
              labelText={ `${ t('profile.anotherEmailAddress') }:` }
              checked={ additionalChecked }
              onChange={ setAdditionalChecked }
            />
            <NWTextInput
              id="additionalEmail"
              name="additionalEmail"
              labelText=""
              invalid={ !!errors.additionalEmail?.message.length }
              invalidText={ errors.additionalEmail?.message }
              disabled={ !additionalChecked }
              { ...register('additionalEmail', {
                required: { value: additionalChecked, message: t('errors.fieldBlank') },
                pattern: { value: EMAIL_REGEX, message: t('errors.invalidEmail') },
              }) }
            />
          </div>
          <div className="actions">
            <Button
              kind="danger"
              onClick={ handleConfirm }
              disabled={
                (additionalChecked && (!additionalEmail || errors?.additionalEmail?.message?.length))
                || !(primaryChecked || additionalChecked)
              }
            >
              { isLoading && <StyledLoading small withOverlay={ false } />}
              { t('common.send') }
            </Button>
            <Button
              className="cancel"
              kind="ghost"
              onClick={ handleClose }
            >
              { t('common.cancel') }
            </Button>
          </div>
        </StyledModalBody>
      </StyledComposedModal>
    </ModalManager>
  );
};

StockAlertModal.propTypes = {
  open: bool,
  code: string,
  primaryEmail: string,
  primaryChecked: bool,
  setPrimaryChecked: func,
  additionalEmail: string,
  additionalChecked: bool,
  setAdditionalChecked: func,
  isLoading: bool,
  handleConfirm: func,
  handleClose: func,
  register: func,
  errors: object,
};

StockAlertModal.displayName = 'StockAlertModalComponent';

export default StockAlertModal;
